import { useEffect } from 'react';

import WebApp from '@twa-dev/sdk';

import { useIsAnyModalOpened } from '@/app/modals/modals-store';

import { isTMA } from '@/shared/helpers/is-tma';

const TG_MAIN_BUTTON_DISABLED_COLOR = '#E8E8E9';
const TG_MAIN_BUTTON_DISABLED_TEXT_COLOR = '#B9B9BA';

interface TelegramMainButtonOptions {
  color?: `#${string}`;
  textColor?: `#${string}`;

  disabled?: boolean;
  hidden?: boolean;
}

// TODO: Find a way to handle multiple instances of useTelegramMainButton without side effects
let hideTimer: NodeJS.Timeout | null = null;

const DEV_BUTTON_ID = 'dev-main-button';

export const useTelegramMainButton = (
  callback: () => void,
  text: string,
  { color, textColor, disabled, hidden: hiddenProp }: TelegramMainButtonOptions = {}
) => {
  const isAnyModalOpened = useIsAnyModalOpened();

  const hidden = hiddenProp || isAnyModalOpened;

  const IS_DEV = import.meta.env.DEV && !isTMA();

  const defaultColor = color ?? WebApp?.themeParams?.button_color;
  const defaultTextColor = textColor ?? WebApp?.themeParams?.button_text_color;

  const clearDevButton = () => {
    document.documentElement.style.removeProperty('--dev-page-bottom-offset');

    try {
      const devButton = document.getElementById(DEV_BUTTON_ID);
      if (devButton) document.body.removeChild(devButton);
    } catch {
      /* empty */
    }
  };

  // Handle calling the hook from different pages.
  useEffect(() => {
    // If there is main button on the current and previous page,
    // clear timeout and keep main button on the screen
    if (hideTimer) clearTimeout(hideTimer);

    return () => {
      if (IS_DEV) clearDevButton();

      // Set a new timer when you go to the next page
      hideTimer = setTimeout(() => {
        // If there is no instances of the hook then hide main button
        WebApp.MainButton.hide();
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    WebApp.onEvent('mainButtonClicked', callback);

    return () => {
      WebApp.offEvent('mainButtonClicked', callback);
    };
  }, [callback]);

  useEffect(() => {
    if (hidden) {
      WebApp.MainButton.hide();
    } else if (!WebApp.MainButton.isVisible) {
      WebApp.MainButton.show();

      // To avoid default text for button
      WebApp.MainButton.setParams({
        text,

        ...(disabled
          ? {
              is_active: false,
              color: TG_MAIN_BUTTON_DISABLED_COLOR,
              textColor: TG_MAIN_BUTTON_DISABLED_TEXT_COLOR,
            }
          : {
              is_active: true,
              color: defaultColor,
              textColor: defaultTextColor,
            }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  useEffect(() => {
    if (hidden) return;

    if (disabled) {
      WebApp.MainButton.disable();
    } else if (!WebApp.MainButton.isActive) {
      WebApp.MainButton.enable();
    }
  }, [disabled, hidden]);

  useEffect(() => {
    if (hidden) return;

    WebApp.MainButton.setParams({
      text,

      ...(disabled
        ? {
            is_active: false,
            color: TG_MAIN_BUTTON_DISABLED_COLOR,
            textColor: TG_MAIN_BUTTON_DISABLED_TEXT_COLOR,
          }
        : {
            is_active: true,
            color: defaultColor,
            textColor: defaultTextColor,
          }),
    });
  }, [defaultColor, defaultTextColor, disabled, text, hidden]);

  // Fake main button for development in browser
  useEffect(() => {
    if (!IS_DEV) return;

    let button = document.getElementById(DEV_BUTTON_ID) as HTMLButtonElement | undefined;

    if (!button) {
      button = document.createElement('button');
      button.id = DEV_BUTTON_ID;

      document.body.appendChild(button);
    }

    button.disabled = !!disabled;
    button.hidden = !!hidden;
    button.textContent = text;
    button.className = 'dev-main-button';

    button.onclick = callback;

    if (hidden) {
      document.documentElement.style.removeProperty('--dev-page-bottom-offset');
    } else {
      document.documentElement.style.setProperty('--dev-page-bottom-offset', '60px');
    }
  }, [IS_DEV, callback, disabled, hidden, text]);
};
